<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('warehouse.return_goods')"
    :subtitle="editedItem && editedItem.id ? 'ID: ' + editedItem.id : undefined"
    icon="mdi-backup-restore"
    color="pink"
    :save-text="$t('warehouse.return')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="save"
  >
    <template v-slot:app-bar-actions>
      <excel-button class="mr-2" @click="exportExcel" />
      <warehouse-button-print-menu @print="print" />
    </template>

    <template v-if="editedItem?.id" v-slot:footer-actions>
      <v-btn
        color="pink"
        depressed
        large
        dark
        @click="update"
      >
        <v-icon left>mdi-content-save-edit</v-icon>
        {{ $t('update') }}
      </v-btn>
    </template>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" lg="3" xl="2">
          <choose-warehouse-field
            return-object
            v-model="editedItem.warehouse"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <date-picker v-model="editedItem.document_date" single-date color="white" />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <autocomplete
            v-model="editedItem.dispatched_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.returned_product')"
            :rules="[rules.required]"
            color="white"
            required
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <text-area
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
            color="white"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-document-groceries-table
      v-if="selectedWarehouse"
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      :headers="headers"
      return
      @removeGrocery="removeGrocery"
    />
<!--
    <v-alert
      v-if="lastStocktakingDate"
      type="warning"
      dense
      text
      class="mt-3"
      icon="mdi-clipboard-alert-outline"
    >
      {{ $t(stocktakingHintKey).replace("{0}", lastStocktakingDate) }}
    </v-alert> -->
  </fullscreen-dialog>
</template>

<script>
import hasLastStocktaking from "@/mixins/warehouse/hasLastStocktaking";
import hasWarehouseDocumentDialog from "@/mixins/warehouse/hasWarehouseDocumentDialog";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasExcel from "@/mixins/hasExcel";
import { returnDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehouseReturnDialog",
  mixins: [hasWarehouseDocumentDialog, hasLastStocktaking, hasPrintingV2, hasExcel],
  props: {
    editedItem: {
      type: Object,
      default: returnDocumentGrocery,
    },
  },
  methods: {
    update() {
      this.showInfoSnackbar("Ažuriranje povrata je još u razvoju. Funkcionalnost će biti dostupna uskoro.");
    },
    print(withStock = false) {
      const { row, col, field, text, table, line, spacer, heading } = this.printElements;

      const warehouseName = this.toLatin(this.editedItem.warehouse?.name || "");
      const docDate = this.localeDate(this.editedItem.document_date);
      const docNumber = this.editedItem.number || "";
      const returnedByName = this.getUserFullName(this.editedItem.dispatched_by);
      const noteHtml = this.editedItem.notes
        ? text(this.$t("note") + ": " + this.editedItem.notes, {
            size: 12,
            block: true,
            mt: 20,
          })
        : "";

      this.printDoc(
        [
          // Header: warehouse + date
          row("between align-end", [
            field(warehouseName, this.$t("stock"), 300),
            field(docDate, this.$t("date"), 120),
          ]),
          // Title
          spacer(14),
          heading(this.$t("warehouse.return_number") + " " + docNumber),
          // Table
          spacer(10),
          table(this.getPrintHeaders(withStock, false), this.getPrintItems(false)),
          // Note
          noteHtml,
          // Signature
          spacer(40),
          row("between", [
            col("center", [
              text(this.$t("warehouse.returned_product")),
              spacer(16),
              line(),
              text(returnedByName),
            ]),
          ]),
        ],
        { pageTitle: this.$t("warehouse.return_number") + " " + docNumber }
      );
    },
    async exportExcel() {
      const docNumber = this.editedItem.number || "";
      const title = this.$t("warehouse.return_number") + " " + docNumber;
      await this.downloadExcel({
        fileName: `${title}.xlsx`,
        sheets: [
          {
            name: title.substring(0, 31),
            title,
            headers: this.getPrintHeaders(false, false),
            items: this.getPrintItems(false),
          },
        ],
      });
    },
    async save() {
      const groceries = this.editedItem.groceries.map((item) => ({
        grocery_id: item.id,
        warehouse_id: this.editedItem.warehouse.id,
        name: item.name,
        code: item.code,
        supplier_code: item.supplier_code,
        amount: item.amount,
        measure: item.measure,
        transaction_type: "entry",
        document_type: "return",
      }));
      const params = {
        ...this.editedItem,
        warehouse_id: this.editedItem.warehouse.id,
        groceries,
        document_type: "return",
      };

      const request = warehouseService.addDocumentTransaction(params);
      try {
        this.loading = true;
        const { data } = await this.handleRequest(request, {
          successMessage: this.$t("warehouse.return_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.$emit("groceriesReturned", data);
        this.$emit("success");
        this.model = false;
      } catch (error) {
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    stocktakingHintKey() {
      return "warehouse.last_stocktaking_hint_return";
    },
  },
};
</script>
